import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Contract, Milestone } from '../types';
import { contractViewState, stageSummaryStatus, type StageSummaryStatus } from '../utils/contractOverview';
import { replacementLabel } from '../utils/milestoneHistory';

const STAGE_TONE: Record<StageSummaryStatus, string> = {
  paid: 'success',
  attested: 'success',
  attesting: 'warning',
  disputed: 'danger',
  inProgress: 'primary',
  notStarted: 'secondary',
};

export interface ContractSummaryAttachment {
  id: string;
  originalName: string;
  size?: number;
}

interface ContractSummaryProps {
  contract: Contract;
  milestones: Milestone[];
  attachments: ContractSummaryAttachment[];
  formatAmount: (value: number) => string;
  formatSize: (bytes: number) => string;
  onOpenAttachment: (attachment: ContractSummaryAttachment) => void;
  /** An ÄTA's own figures in place of the contract's (ClickUp 869fmxfb7). */
  keyFigures?: React.ReactNode;
  /** What an ÄTA changes, in place of the stage list. */
  changes?: React.ReactNode;
  /** How it works, folded up, last in the card. */
  help?: React.ReactNode;
}

const amount = (value: string | number | null | undefined) => Number(value) || 0;

/**
 * What the contract says, read-only, for both parties and for ÄTA
 * (ClickUp 869fa5qf8): the amounts, the stages with their state and the
 * attachments, which open in the document preview. Editing happens in the
 * contract wizard; a signed contract's binding text is the signed PDF.
 */
export function ContractSummary({
  contract, milestones, attachments, formatAmount, formatSize, onOpenAttachment,
  keyFigures, changes, help,
}: ContractSummaryProps) {
  const { t } = useTranslation();
  const state = contractViewState(contract);
  const plannedStages = milestones.filter(milestone => (milestone.itemType ?? 'milestone') === 'milestone');
  const stages = plannedStages.filter(milestone => !milestone.stageReplacement);
  // Stages an ÄTA replaced or removed stay readable here, greyed out: the old
  // stage list is hidden in the status view.
  const replacedStages = plannedStages.filter(milestone => milestone.stageReplacement);
  // The key figures, most important first (ClickUp 869fmxfb7): what each
  // party pays is on the Transaktioner tab.
  const gross = amount(contract.contractAmount);
  const afterRot = amount(contract.afterRotAmount ?? contract.contractAmount);

  return (
    <div className="contract-summary" data-testid="contract-summary">
      {keyFigures ?? (
        <dl className="contract-summary-figures">
          <div><dt>{t('contracts.summary.amount')}</dt><dd>{formatAmount(gross)}</dd></div>
          <div><dt>{t('contracts.summary.amountAfterRot')}</dt><dd>{formatAmount(afterRot)}</dd></div>
          <div><dt>{t('contracts.summary.stageCount')}</dt><dd>{stages.length}</dd></div>
        </dl>
      )}
      <div className="contract-summary-body">
        <p className="contract-summary-hint">{t(`contracts.summary.hint.${state}`)}</p>

        {changes ?? <><h3 className="contract-summary-heading">{t('contracts.summary.stagesTitle', { count: stages.length })}</h3>
        {stages.length === 0 ? (
          <p className="text-muted small mb-0">{t('contracts.summary.noStages')}</p>
        ) : (
          <ul className="contract-summary-stages" data-testid="contract-summary-stages">
            {stages.map(stage => {
              const status = stageSummaryStatus(stage);
              return (
                <li key={stage.id} data-summary-stage={stage.id}>
                  <span className="contract-summary-stage-name">
                    {t('contracts.summary.stageName', { number: stage.milestoneNumber, description: stage.description })}
                    {stage.endDate && <small>{t('contracts.summary.stageEnd', { date: stage.endDate })}</small>}
                  </span>
                  <span className="contract-summary-stage-amount">{formatAmount(amount(stage.afterRotAmount ?? stage.amount))}</span>
                  <span className={`contract-group-status-pill is-${STAGE_TONE[status]}`}>{t(`contracts.summary.stageStatus.${status}`)}</span>
                </li>
              );
            })}
          </ul>
        )}

        {replacedStages.length > 0 && <>
          <h3 className="contract-summary-heading">{t('contracts.summary.replacedTitle', { count: replacedStages.length })}</h3>
          <ul className="contract-summary-stages is-replaced" data-testid="contract-summary-replaced">
            {replacedStages.map(stage => (
              <li key={stage.id} data-summary-replaced-stage={stage.id}>
                <span className={`contract-summary-stage-name ${stage.stageReplacement?.kind === 'removed' ? 'text-decoration-line-through' : ''}`}>
                  {t('contracts.summary.stageName', { number: stage.milestoneNumber, description: stage.description })}
                </span>
                <span className="contract-summary-stage-amount">{formatAmount(amount(stage.afterRotAmount ?? stage.amount))}</span>
                <span className="contract-group-status-pill">{replacementLabel(stage, t)}</span>
              </li>
            ))}
          </ul>
        </>}
        </>}

        <h3 className="contract-summary-heading">{t('contracts.summary.filesTitle', { count: attachments.length })}</h3>
        {attachments.length === 0 ? (
          <p className="text-muted small mb-0">{t('contracts.summary.noFiles')}</p>
        ) : (
          <ul className="contract-summary-files" data-testid="contract-summary-files">
            {attachments.map(file => (
              <li key={file.id}>
                <button type="button" className="contract-summary-file" onClick={() => onOpenAttachment(file)}>
                  <i className="feather-file-text" aria-hidden="true"></i>
                  <span className="contract-summary-file-name">{file.originalName}</span>
                  {file.size ? <small>{formatSize(file.size)}</small> : null}
                </button>
              </li>
            ))}
          </ul>
        )}

        {help && <div className="contract-summary-help">{help}</div>}
      </div>
    </div>
  );
}
